/**
 * ADR-0058: measuring Townsy's ads on X (founder 2026-11-04). The API decides who gets X's script and whose purchases
 * are reported; the web app keeps X's script off private pages. Both read the rules here.
 */

/** The founder's X pixel. */
export const X_PIXEL_ID = "riigz";

/** The API route that serves X's base code, or an empty script to anyone who must not get it. */
export const X_PIXEL_SCRIPT_PATH = "/api/v1/x-pixel.js";

/** Set to "1" by the switch on the Privacy page: this browser opted out of ad measurement. */
export const AD_OPT_OUT_COOKIE = "townsy_ads_opt_out";

/** X's click id from the ad's landing address, kept until a purchase can report it. */
export const X_CLICK_ID_COOKIE = "townsy_twclid";

/** The cookies X's own script keeps on Townsy's domain; the opt-out switch clears them. */
export const X_SCRIPT_COOKIES = ["_twclid", "_twpid", "_twsid"] as const;

/**
 * Pages X's script must never run on. X receives the full page address, the text of clicked buttons and any email,
 * phone number or name in a form, so these stay out: password reset links and payment returns carry secrets in the
 * address, the account and order pages hold personal details, and review and admin hold other people's data.
 * React Router matches paths in any case, so this does too.
 */
const PRIVATE_PAGE = /^\/(?:reset-password|account|orders|review|admin)(?:\/|$)/i;

export function isPrivatePagePath(pathname: string): boolean {
  return PRIVATE_PAGE.test(pathname);
}

/**
 * Founder 2026-11-04: no ad measurement for visitors here (no consent banner exists): the EU, the EEA, the UK and
 * Switzerland, with the territories Cloudflare reports under their own codes.
 */
const NO_AD_MEASUREMENT_COUNTRIES: ReadonlySet<string> = new Set([
  // EU
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE",
  // EU territories with their own country codes
  "AX", "GF", "GP", "MF", "MQ", "RE", "YT",
  // EEA
  "IS", "LI", "NO",
  // UK and its Crown dependencies and Gibraltar
  "GB", "GG", "GI", "IM", "JE",
  // Switzerland
  "CH",
]);

export function isNoAdMeasurementCountry(countryCode: string): boolean {
  return NO_AD_MEASUREMENT_COUNTRIES.has(countryCode.toUpperCase());
}

/** A click id is copied from a URL, so it is untrusted: only this shape is kept or sent on. */
export function isValidXClickId(value: string): boolean {
  return /^[A-Za-z0-9_-]{1,255}$/.test(value);
}
